import axios from "axios";
import {
  ExceptionTypes,
  exceptionStatusCodes,
  whiteListCSRF,
  headerCsrf,
  url,
  requestTimeout,
  MODAL_NAME,
  localStorageKey,
  localStorageBoolean,
} from "./utils/constants";
import store from "./store";
import router from "./router";
import { homeRoute, Route, UserRoute } from "./services/apiRoutes/routes";
import { get } from "lodash";
import { IToastMessage, ToastVariant } from "./interfaces/common/IToastMessage";
import EventEmitter, { EmitterEvents } from "./utils/eventEmitter";
import { getComponentUiText } from "./utils/utilities";
import { CustomExceptionsUIText } from "./services/apiRoutes/customExceptions";
import { IModalMessage } from "./interfaces/common/common";
import { showError, showErrorAPI } from "./services/apiRoutes";
import { getSessionPortal } from "./services/subscriptions";
import { ref } from "vue";

const controller = ref(new AbortController());

const getCSRF = () => {
  return axios.get(`${url}${Route.csrf}`, { withCredentials: true });
};

const getCsrfTokenFromCookie = async () => {
  const cookies = document.cookie.split(";");

  for (const cookie of cookies) {
    const [name] = cookie.trim().split("=");
    if (name === headerCsrf) {
      return true;
      //  await getCSRf();
    }
  }

  // If CSRF token is not found in cookie, make an API request to get it
  try {
    await getCSRF();
    return true;
  } catch (error) {
    return false;
  }
};

const api = axios.create({
  baseURL: url,
  timeout: requestTimeout,
  headers: {
    "Content-Type": "application/json",
  },
  signal: controller.value.signal,
});

const handleAPIInvalidToken = () => {
  store.dispatch("User/unauthenticateUser");
  store.dispatch("Prospect/unauthenticateProspect");

  const message = getComponentUiText(
    CustomExceptionsUIText[ExceptionTypes.InvalidSession],
  );
  const emitData: IToastMessage = { variant: ToastVariant.ERROR, message };
  EventEmitter.emit(EmitterEvents.APP_LAYOUT_SHOW_TOAST_MESSAGE, emitData);
  if (
    router.currentRoute?.value.path !== "/assess/vendor/external/input" &&
    router.currentRoute?.value.path !== "/assess/vendor/external/sorry"
  ) {
    router.push({ path: "/user/login" });
  }
};
const handleOrgPlanNotEntitled = () => {
  const emitData: IModalMessage = {
    component: MODAL_NAME.MODAL_RECOMMEN_ARE_YOU_SURE,
    properties: {
      value: {},
      title: getComponentUiText("app-subscription-required-modal-title-1"),
      description: getComponentUiText(
        "app-subscription-required-modal-description-1",
      ),
      list: [],
      buttonText: getComponentUiText(
        "app-subscription-required-button-title-1",
      ),
      isShowBtnCancel: false,
      buttonRed: false,
      hiddenHelp: false,
    },
    events: {
      "update:data": async () => {
        try {
          {
            const clientSecretResponse = await getSessionPortal("dashboard");
            const sessionPortalUrl =
              clientSecretResponse.data?.data?.redirectUrl || "";
            if (!sessionPortalUrl) {
              showErrorAPI("app-settings-subscription-error-text-1", "error");
              return;
            }
            window.location.href = sessionPortalUrl;
            return;
          }
        } catch (error) {
          // showErrorAPI(error, Subscription.getSessionPortal);
        }
      },
      "update:close": (isClose) => {
        if (!isClose) {
          showError("app-api-org-entitle-upgrade-error-1", "");
          router.push({ path: homeRoute.homePage });
          return;
        }
        const routes = router.currentRoute?.value.path || "";
        if (routes === homeRoute.homePage) {
          localStorage.setItem(
            localStorageKey.forcePricingTable,
            localStorageBoolean.true,
          );
          EventEmitter.emit(EmitterEvents.SHOW_PRICING_TABLE);
        } else {
          localStorage.setItem(
            localStorageKey.forcePricingTable,
            localStorageBoolean.true,
          );
          router.push({ path: homeRoute.homePage });
        }
      },
    },
  };
  EventEmitter.emit(EmitterEvents.OPEN_MODAL, emitData);
};

const handleAnauthorized = () => {
  const emitData: IModalMessage = {
    component: MODAL_NAME.MODAL_HANDLE_ANAUTH,
    properties: {
      value: {},
      title: getComponentUiText("app-handle-error-unauthorized-modal-title-1"),
      description: getComponentUiText(
        "app-handle-error-unauthorized-modal-description-1",
      ),
      list: [],
      buttonText: getComponentUiText(
        "app-assess-recommendations-action-table-bulk-actions-modal-button-add-no-action-1",
      ),
      isShowBtnCancel: false,
      buttonRed: false,
      hiddenHelp: false,
    },
    events: {},
  };
  EventEmitter.emit(EmitterEvents.OPEN_MODAL, emitData);
};

const handleNotFoundAPIError = () => {
  const message = getComponentUiText("app-api-error-not-found-1");
  const emitData: IToastMessage = { variant: ToastVariant.ERROR, message };
  EventEmitter.emit(EmitterEvents.APP_LAYOUT_SHOW_TOAST_MESSAGE, emitData);
};

const recreateAbortController = () => {
  controller.value = new AbortController();
  api.defaults.signal = controller.value.signal;
};

export const cancelAllRequests = () => {
  controller.value.abort();
  recreateAbortController();
};

api.interceptors.request.use(
  async (config) => {
    const activeCallApi = store.state.User.activeCallApi;
    if (!activeCallApi) {
      return Promise.reject(new axios.Cancel(`Request rejected ${config.url}`));
    }
    if (config.url === UserRoute.logout) {
      store.dispatch("User/setActiveCallApi", false);
    }
    if (config.url) {
      config.withXSRFToken = true;
      config.withCredentials = true;
    } else {
      if (config.withCredentials) {
        delete config.withXSRFToken;
        delete config.withCredentials;
      }
    }

    // Check if URL is in white list
    if (!whiteListCSRF.includes(`${config.url}`)) {
      // Check if a CSRF token cookie exists
      await getCsrfTokenFromCookie();
    }

    return Promise.resolve(config);
  },
  function (err) {
    return Promise.reject(err);
  },
);

api.interceptors.response.use(
  function async(response) {
    return response;
  },

  function (error: any) {
    if (axios.isCancel(error)) {
      return Promise.reject("Request cancelled");
    }
    const errorStatus = get(error, "response.status");
    const errorType = get(error, "response.data.error.type", "");

    if (
      errorType === ExceptionTypes.InvalidSession ||
      errorType === ExceptionTypes.OidCookieNotPresent
    )
      handleAPIInvalidToken();
    else if (errorStatus === exceptionStatusCodes.NotFoundRequest)
      handleNotFoundAPIError();
    if (
      errorType === ExceptionTypes.OrgPlanNotEntitled ||
      errorType === ExceptionTypes.OrgSwitchPlanNotEntitled
    ) {
      store.dispatch("Org/setCurrentOrgPlanDetails", {});
      const isForcingPricingTable = localStorage.getItem(
        localStorageKey.forcePricingTable,
      );
      setTimeout(() => {
        const routes = router.currentRoute?.value.path || "";
        if (!isForcingPricingTable && routes !== homeRoute.homePage) {
          handleOrgPlanNotEntitled();
        }
      }, 1000);
      return Promise.reject(null);
    }

    if (errorType === ExceptionTypes.UserUnauthorized) {
      handleAnauthorized();
      return Promise.reject(null);
    }

    return Promise.reject(error.response);
  },
);
export default api;
